import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CreditPlanFields } from '../types/clientAccount';

// Resumen de créditos «dos bolsas» (plan mensual + extra), compartido por el
// detalle admin (ClientAccountDetailPage) y el portal cliente
// (CustomerAccountPage). Los tres números: barra de progreso del PLAN
// («X de N usados · se renueva el {fecha}», oculta si plan_total=0),
// «Extra: Y» y «Disponible total: Z» destacado.
//
// Tolerante al backend en construcción: si los campos nuevos no llegan
// (available_total ausente/null) cae al saldo simple `balance` — y, si se
// proporciona, al «Consumido» acumulado — sin romper.

const formatCredits = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

const toNum = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// 'YYYY-MM-DD' se ancla a medianoche LOCAL (sin el T00:00:00, JS lo parsea
// como UTC y en husos negativos se pintaría el día anterior).
const formatRenewDate = (raw: string): string | null => {
  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

interface CreditsSummaryProps {
  // Cualquier objeto que lleve (o pueda llevar) los campos del plan:
  // ClientAccountDetail, CustomerAccountSummary, CustomerCreditsResponse…
  data?: CreditPlanFields | null;
  // Saldo simple actual — fallback cuando el backend aún no habla «dos bolsas».
  balance?: number | string | null;
  // Consumido acumulado — solo se pinta en modo fallback (con plan, el
  // consumo del ciclo ya lo cuenta la barra del plan).
  consumedTotal?: number | string | null;
}

const CreditsSummary: React.FC<CreditsSummaryProps> = ({ data, balance, consumedTotal }) => {
  const { t } = useTranslation();

  const planTotal = toNum(data?.plan_total);
  const planUsed = toNum(data?.plan_used) ?? 0;
  const extraBalance = toNum(data?.extra_balance);
  const availableTotal = toNum(data?.available_total);

  // Señal de que el backend ya devuelve el modelo «dos bolsas».
  const hasTwoBags = availableTotal !== null;

  if (!hasTwoBags) {
    // Fallback: saldo simple actual (comportamiento previo), sin romper.
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('creditsSummary.balance', 'Saldo')}
        </Typography>
        <Typography variant="h4" fontWeight={700} color="primary.main">
          {formatCredits(balance)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('creditsSummary.creditsUnit', 'créditos')}
        </Typography>
        {/* SIN leyenda de tasa: este bloque habla de CONSUMO (saldo que se
            drena con el uso real), no de precio. Enseñar aquí una equivalencia
            en dólares mezclaba dos cosas distintas — el coste que representa un
            crédito y lo que cuesta comprarlo (CREDIT_PRICE_USD) — y hacía creer
            al cliente que su consumo se factura a precio de venta. El precio
            vive donde se factura: el statement del partner. */}
        {consumedTotal !== null && consumedTotal !== undefined && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('creditsSummary.consumed', 'Consumido')}:{' '}
            <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
              {formatCredits(consumedTotal)}
            </Typography>
          </Typography>
        )}
      </Box>
    );
  }

  const hasPlan = planTotal !== null && planTotal > 0;
  const planPct = hasPlan ? Math.min(100, Math.max(0, (planUsed / (planTotal as number)) * 100)) : 0;
  const renewDate = data?.plan_renews_at ? formatRenewDate(data.plan_renews_at) : null;

  return (
    <Box>
      {hasPlan && (
        <Box mb={1.5}>
          <Typography variant="body2" color="text.secondary">
            {t('creditsSummary.plan', 'Plan mensual')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={planPct}
            sx={{ height: 8, borderRadius: 4, my: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            {t('creditsSummary.planUsage', '{{used}} de {{total}} usados', {
              used: formatCredits(planUsed),
              total: formatCredits(planTotal)
            })}
            {renewDate
              ? ` · ${t('creditsSummary.renewsAt', 'se renueva el {{date}}', { date: renewDate })}`
              : ''}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" mb={1}>
        {t('creditsSummary.extra', 'Extra')}:{' '}
        <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
          {formatCredits(extraBalance ?? 0)}
        </Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('creditsSummary.availableTotal', 'Disponible total')}
      </Typography>
      <Typography variant="h4" fontWeight={700} color="primary.main">
        {formatCredits(availableTotal)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('creditsSummary.creditsUnit', 'créditos')}
      </Typography>
      {/* Ver el comentario del fallback: aquí se habla de consumo, no de
          precio, así que no se pinta ninguna equivalencia en dólares. */}
    </Box>
  );
};

export default CreditsSummary;
